import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotification } from '../contexts/NotificationContext';
import { DASHBOARD_ACCESS_LEVELS, buildGroupTree } from '../utils/dashboardAccess';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

function managerLabel(m) {
  const full = `${m.name || ''} ${m.surname || ''}`.trim();
  if (full && m.email) return `${full} (${m.email})`;
  return full || m.email || m.username || m.id;
}

function matchesSearch(text, q) {
  if (!q) return true;
  return String(text || '').toLowerCase().includes(q);
}

const DashboardAccessGroupsAdmin = ({
  getAuthHeaders,
  managers,
  dashboards,
  groups,
  accessByGroup,
  managersByGroup,
  onReload,
}) => {
  const { showSuccess, showError } = useNotification();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupSearch, setGroupSearch] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [dashboardSearch, setDashboardSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editParentId, setEditParentId] = useState('');
  const [localManagerIds, setLocalManagerIds] = useState([]);
  const [localGrants, setLocalGrants] = useState([]);
  const [dirty, setDirty] = useState(false);

  const groupTree = useMemo(() => buildGroupTree(groups), [groups]);

  const filteredGroupTree = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groupTree;
    return groupTree.filter((g) => matchesSearch(g.name, q));
  }, [groupTree, groupSearch]);

  const parentOptions = useMemo(
    () => groups.filter((g) => String(g.id) !== String(selectedGroupId)),
    [groups, selectedGroupId]
  );

  const selectedGroup = selectedGroupId != null;

  const filteredManagers = useMemo(() => {
    const q = managerSearch.trim().toLowerCase();
    return (managers || []).filter((m) =>
      matchesSearch([m.name, m.surname, m.email, m.username, m.id].join(' '), q)
    );
  }, [managers, managerSearch]);

  const filteredDashboards = useMemo(() => {
    const q = dashboardSearch.trim().toLowerCase();
    return (dashboards || []).filter((d) =>
      matchesSearch([d.name, d.publicId, d.database, d.id].join(' '), q)
    );
  }, [dashboards, dashboardSearch]);

  const selectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    const g = groups.find((x) => String(x.id) === String(groupId));
    setEditName(g?.name || '');
    setEditDescription(g?.description || '');
    setEditParentId(g?.parentGroupId != null ? String(g.parentGroupId) : '');
    setLocalManagerIds(managersByGroup[String(groupId)] || []);
    setLocalGrants(accessByGroup[String(groupId)] || []);
    setDirty(false);
  };

  const createGroupMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/admin/dashboard-access/groups', body, { headers }),
  });

  const handleCreateGroup = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    const name = window.prompt('Nombre del nuevo grupo:');
    if (!name?.trim()) return;
    setCreating(true);
    try {
      const res = await createGroupMutation.mutateAsync({
        body: { name: name.trim(), parentGroupId: selectedGroupId || null },
        headers,
      });
      showSuccess(`Grupo «${name.trim()}» creado`);
      await onReload?.();
      if (res.data?.group?.id) selectGroup(res.data.group.id);
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'No se pudo crear el grupo');
    } finally {
      setCreating(false);
    }
  };

  const deleteGroupMutation = useMutation({
    mutationFn: ({ groupId, headers }) => axios.delete(`/api/admin/dashboard-access/groups/${groupId}`, { headers }),
  });

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;
    if (!window.confirm(`¿Eliminar el grupo «${editName}»? Los subgrupos pasarán al padre de este grupo.`)) {
      return;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setSaving(true);
    try {
      await deleteGroupMutation.mutateAsync({ groupId: selectedGroupId, headers });
      showSuccess('Grupo eliminado');
      setSelectedGroupId(null);
      await onReload?.();
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'No se pudo eliminar el grupo');
    } finally {
      setSaving(false);
    }
  };

  const toggleManager = (managerId) => {
    setLocalManagerIds((prev) => {
      const set = new Set(prev);
      if (set.has(managerId)) set.delete(managerId);
      else set.add(managerId);
      return [...set];
    });
    setDirty(true);
  };

  const grantForDashboard = (dashboardId) =>
    localGrants.find((g) => Number(g.dashboardId) === Number(dashboardId));

  const toggleDashboard = (dashboardId, checked) => {
    setLocalGrants((prev) => {
      const id = Number(dashboardId);
      const without = prev.filter((g) => Number(g.dashboardId) !== id);
      if (!checked) return without;
      return [...without, { dashboardId: id, accessLevel: 'view' }];
    });
    setDirty(true);
  };

  const setDashboardLevel = (dashboardId, accessLevel) => {
    const id = Number(dashboardId);
    setLocalGrants((prev) => {
      const without = prev.filter((g) => Number(g.dashboardId) !== id);
      return [...without, { dashboardId: id, accessLevel: accessLevel === 'edit' ? 'edit' : 'view' }];
    });
    setDirty(true);
  };

  const updateGroupMutation = useMutation({
    mutationFn: ({ groupId, suffix, body, headers }) =>
      axios.put(`/api/admin/dashboard-access/groups/${groupId}${suffix}`, body, { headers }),
  });

  const handleSaveGroup = async () => {
    if (!selectedGroupId) return;
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setSaving(true);
    try {
      await updateGroupMutation.mutateAsync({
        groupId: selectedGroupId,
        suffix: '',
        body: {
          name: editName.trim(),
          description: editDescription.trim(),
          parentGroupId: editParentId || null,
        },
        headers,
      });
      await updateGroupMutation.mutateAsync({
        groupId: selectedGroupId,
        suffix: '/members',
        body: { managerIds: localManagerIds },
        headers,
      });
      await updateGroupMutation.mutateAsync({
        groupId: selectedGroupId,
        suffix: '/dashboards',
        body: { grants: localGrants },
        headers,
      });
      showSuccess('Grupo guardado');
      setDirty(false);
      await onReload?.();
      selectGroup(selectedGroupId);
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'No se pudo guardar el grupo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Organiza managers en grupos jerárquicos. Un manager hereda los dashboards de su grupo y de todos
        los grupos padre. Asigna el nivel de permiso por dashboard: «Solo lectura» o «Edición».
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '240px 1fr 1fr' },
          gap: 2,
          minHeight: 400,
        }}
      >
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Grupos
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar grupo..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
              onClick={handleCreateGroup}
              sx={{ mt: 1 }}
              disabled={creating}
            >
              Nuevo grupo
            </Button>
          </Box>
          <List dense sx={{ overflow: 'auto', flex: 1 }}>
            {filteredGroupTree.length === 0 && (
              <ListItem>
                <ListItemText primary="Sin grupos" secondary="Crea el primero" />
              </ListItem>
            )}
            {filteredGroupTree.map((g) => (
              <ListItemButton
                key={g.id}
                selected={String(selectedGroupId) === String(g.id)}
                onClick={() => selectGroup(g.id)}
                dense
                sx={{ pl: 1.5 + g.depth * 2 }}
              >
                <ListItemText primary={g.name} secondary={g.description || null} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Managers del grupo
            </Typography>
            {!selectedGroup ? (
              <Typography variant="caption" color="text.secondary">
                Selecciona un grupo
              </Typography>
            ) : (
              <>
                <TextField
                  label="Nombre"
                  size="small"
                  fullWidth
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setDirty(true);
                  }}
                  sx={{ mb: 1 }}
                />
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <InputLabel>Grupo padre</InputLabel>
                  <Select
                    label="Grupo padre"
                    value={editParentId}
                    onChange={(e) => {
                      setEditParentId(e.target.value);
                      setDirty(true);
                    }}
                  >
                    <MenuItem value="">(raíz)</MenuItem>
                    {parentOptions.map((g) => (
                      <MenuItem key={g.id} value={String(g.id)}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Descripción"
                  size="small"
                  fullWidth
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    setDirty(true);
                  }}
                  sx={{ mb: 1 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Buscar manager..."
                  value={managerSearch}
                  onChange={(e) => setManagerSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Box>
          <List dense sx={{ overflow: 'auto', flex: 1 }}>
            {!selectedGroup && null}
            {selectedGroup &&
              filteredManagers.map((m) => (
                <ListItemButton key={m.id} onClick={() => toggleManager(m.id)} dense>
                  <Checkbox checked={localManagerIds.includes(m.id)} tabIndex={-1} disableRipple />
                  <ListItemText primary={managerLabel(m)} secondary={m.username ? `@${m.username}` : m.id} />
                </ListItemButton>
              ))}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Dashboards del grupo
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar dashboard..."
              value={dashboardSearch}
              onChange={(e) => setDashboardSearch(e.target.value)}
              disabled={!selectedGroup}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List dense sx={{ overflow: 'auto', flex: 1 }}>
            {!selectedGroup && (
              <ListItem>
                <ListItemText primary="Selecciona un grupo" />
              </ListItem>
            )}
            {selectedGroup &&
              filteredDashboards.map((d) => {
                const grant = grantForDashboard(d.id);
                const checked = Boolean(grant);
                return (
                  <ListItemButton
                    key={d.id}
                    dense
                    onClick={() => toggleDashboard(d.id, !checked)}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Checkbox checked={checked} tabIndex={-1} disableRipple sx={{ mt: 0.25 }} />
                    <ListItemText
                      primary={d.name || `Dashboard ${d.id}`}
                      secondary={[d.publicId ? `ID ${d.publicId}` : null, d.database ? `BD ${d.database}` : null]
                        .filter(Boolean)
                        .join(' · ')}
                      sx={{ flex: 1 }}
                    />
                    {checked && (
                      <FormControl size="small" sx={{ minWidth: 120, ml: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={grant.accessLevel || 'view'}
                          onChange={(e) => setDashboardLevel(d.id, e.target.value)}
                        >
                          {Object.entries(DASHBOARD_ACCESS_LEVELS).map(([key, meta]) => (
                            <MenuItem key={key} value={key}>
                              {meta.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </ListItemButton>
                );
              })}
          </List>
        </Paper>
      </Box>

      {selectedGroup && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button color="error" startIcon={<DeleteIcon />} onClick={handleDeleteGroup} disabled={saving}>
            Eliminar grupo
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveGroup}
            disabled={saving || !dirty}
          >
            Guardar grupo
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DashboardAccessGroupsAdmin;
